import { ref } from 'vue';
import type { Reservation, Traveller, Voucher } from '../types';
import { useFetch } from './useFetch';
import type {
  ApplyVoucherRequest,
  ApplyVoucherResponse,
  CompleteReservationRequest,
  CompleteReservationResponse,
  NumberOfTravellersRequest,
  NumberOfTravellersResponse,
  RemoveVoucherRequest,
  RemoveVoucherResponse,
  SetTravellersDataRequest,
  SetTravellersDataResponse,
} from '../requestTypes';
import { useReservationTime } from './useReservationTime';
import { useLocalTravellers } from './useLocalTravellers';
import { useLoader } from './useLoader';
import { useCustomer } from './useCustomer';
import { tracking } from './analytics/analytics';

const { getInitialCustomerData } = useCustomer();

const getReservationStateWithDefaults = (value: Reservation) =>
  ({
    ...value,
    travellers: value?.travellers ?? {},
    vouchers: value?.vouchers ?? [],
    customer: getInitialCustomerData(value?.customer),
    note: '',
    payment_method: 'BANK_TRANSFER',
    newsletter_subscribe: false,
    terms: false,
  }) as Reservation;

const state = ref(
  getReservationStateWithDefaults(window.RESERVATION_STORE.reservation) as Reservation
);

export const useReservation = () => {
  const { trackEvent } = tracking();
  const { protectedFetch } = useFetch();
  const { reservationTimeState } = useReservationTime();
  const {
    localTravellersRef,
    setLocalTravellers,
    getDefaultTravellerData,
    getSingleDefaultTravellerData,
  } = useLocalTravellers();

  const setReservationStateWithDefaults = (value: Reservation) => {
    state.value = getReservationStateWithDefaults(value);
  };

  const setReservationState = (value: Reservation) => {
    state.value = value;
  };

  const setReservationTravellers = (data: Reservation['travellers']) => {
    state.value.travellers = data;
  };

  const { showLoader, hideLoader } = useLoader();

  const setNumberOfTravellers = (value: number) =>
    protectedFetch<NumberOfTravellersRequest, NumberOfTravellersResponse>({
      method: 'post',
      url: window.location.pathname,
      data: {
        travellers_count: value,
      },
    }).then((response) => {
      const { reservation } = response.data;

      const travellersNumber = reservation.requested_travellers_count;
      let travellers = localTravellersRef.value;

      if (response.data.reservation.travellers.constructor === Object) {
        travellers = [];

        Object.values(reservation.travellers).forEach((traveller) => {
          const nextTraveller = { ...getSingleDefaultTravellerData(), ...traveller };
          // nextTraveller.arrayOfServices = traveller.services.constructor === Object ? Object.keys(traveller.services) : [];
          travellers.push(nextTraveller);
        });
      }

      if (travellers.length > travellersNumber) {
        // Remove difference
        travellers = travellers.slice(0, travellersNumber);
      } else if (travellers.length < travellersNumber) {
        // Add difference
        travellers = [
          ...travellers,
          ...getDefaultTravellerData(travellersNumber - travellers.length),
        ];
      }

      if (reservationTimeState.timeUntilReservationEnds === null) {
        reservationTimeState.showRemainingTimeNotice = true;
      }

      setLocalTravellers(travellers);
      setReservationStateWithDefaults(reservation);
      reservationTimeState.timeUntilReservationEnds = response.data.remaining_reservation_time;
    });

  const setTravellersData = async (travellers: Traveller[]) => {
    try {
      const response = await protectedFetch<SetTravellersDataRequest, SetTravellersDataResponse>({
        method: 'post',
        url: '/reservation/travellers-bulk',
        data: {
          travellers: travellers.map((t) => ({
            ...t,
            services: Object.keys(t.services),
          })),
        },
      });

      setReservationTravellers(response.data);

      const newTravellers: Traveller[] = [];

      Object.values(response.data).forEach((traveller) => {
        const nextTraveller = { ...getSingleDefaultTravellerData(), ...traveller };
        // nextTraveller.arrayOfServices = nextTraveller.services.constructor === Object ? Object.keys(nextTraveller.services) : [];
        newTravellers.push(nextTraveller);
      });

      setLocalTravellers(newTravellers);

      return response;
    } catch (e) {
      throw e;
    }
  };

  const setVouchersToReservation = (value: Voucher[]) => {
    let output = value;

    if (output && output.constructor === Object) {
      output = Object.values(output);
    }

    state.value.vouchers = output;
  };

  const applyVoucher = (code: string) =>
    protectedFetch<ApplyVoucherRequest, ApplyVoucherResponse>({
      method: 'post',
      url: '/reservation/voucher/check',
      data: {
        code,
        trip_id: window.RESERVATION_STORE.tripBaseInfo.id,
      },
    }).then((response) => {
      if (response.data.status === 'success') {
        setVouchersToReservation(response.data.vouchers);
      }
      return response;
    });

  const removeVoucher = async (code: string) => {
    showLoader();

    const response = await protectedFetch<RemoveVoucherRequest, RemoveVoucherResponse>({
      method: 'post',
      url: '/reservation/voucher/remove',
      data: {
        code,
      },
    });

    setVouchersToReservation(response.data.vouchers);

    hideLoader();
  };

  const completeReservation = () => {
    protectedFetch<CompleteReservationRequest, CompleteReservationResponse>({
      method: 'post',
      url: '/reservation',
      data: {
        customer: state.value.customer,
        travellers: Object.values(state.value.travellers),
        note: state.value.note as string,
        payment_method: state.value.payment_method as string,
        newsletter_subscribe: state.value.newsletter_subscribe as boolean,
        vouchers: state.value.vouchers,
      },
    }).then((response) => {
      if (response.data.redirect_to) {
        showLoader();
        window.location.href = response.data.redirect_to;
      }
    });
  };

  return {
    reservationState: state,
    setReservationState,
    setNumberOfTravellers,
    setTravellersData,
    applyVoucher,
    removeVoucher,
    completeReservation,
  };
};
